import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDialog } from '../context/DialogContext';
import GalleryModal from './GalleryModal';
import AggregationNodeForm from './AggregationNodeForm';
import { StructureMoveDialog } from './StructureMoveDialog';
import { PremisesBoxDialog } from './PremisesBoxDialog';
import { openNodeMovePanel } from './NodeMovePanel';
import PhotoMiniGallery from './PhotoMiniGallery';
import { usePhotoUpload } from '../hooks/usePhotoUpload';
import '../styles/map-object-popup.css';
const MapObjectPopup = ({ feature, context, onClose }) => {
    const [loading, setLoading] = useState(false);
    const [galleryOpen, setGalleryOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('main');
    const [aggNodes, setAggNodes] = useState([]);
    const [aggNodesLoading, setAggNodesLoading] = useState(false);
    const [aggNodesLoaded, setAggNodesLoaded] = useState(false);
    const [showAggNodeForm, setShowAggNodeForm] = useState(false);
    const [showMoveDialog, setShowMoveDialog] = useState(false);
    const [showPremisesBoxDialog, setShowPremisesBoxDialog] = useState(false);
    const [ctbSummary, setCtbSummary] = useState(null);
    const [editingAggNode, setEditingAggNode] = useState(null);
    const [showMoreActions, setShowMoreActions] = useState(false);
    const [linkCopied, setLinkCopied] = useState(false);
    const [leadinsVisible, setLeadinsVisible] = useState(false);
    const [activeBftIndex, setActiveBftIndex] = useState(0); // For multi-BFT nested tabs
    const [activeConduitIndex, setActiveConduitIndex] = useState(0); // For multi-conduit nested tabs
    const { confirm, info } = useDialog();
    const moreActionsRef = useRef(null);
    const props = feature?.properties || {};
    // Debug logging for BFT
    console.log('[MapObjectPopup] feature:', feature);
    console.log('[MapObjectPopup] props:', props);
    console.log('[MapObjectPopup] props.LAYER:', props.LAYER, 'props.layer:', props.layer);
    console.log('[MapObjectPopup] props._allFeatures:', props._allFeatures);
    // Check for multi-BFT scenario (multiple BFTs on same conduit)
    const allBftFeatures = props._allFeatures;
    const isMultiBft = allBftFeatures && allBftFeatures.length > 1 &&
        (props.layer === 'cfn:bft' || props.LAYER === 'BFT' || props.our_ref || props.OUR_REF);
    console.log('[MapObjectPopup] isMultiBft:', isMultiBft, 'allBftFeatures length:', allBftFeatures?.length);
    // Check for multi-conduit scenario (multiple conduits at structure)
    const allConduitFeatures = props._allFeatures;
    const isMultiConduit = allConduitFeatures && allConduitFeatures.length > 1 &&
        (props.layer === 'cfn:pia_duct' || props.layer === 'cfn:conduit');
    console.log('[MapObjectPopup] isMultiConduit:', isMultiConduit, 'allConduitFeatures length:', allConduitFeatures?.length);
    // Determine object metadata
    const getObjectMetadata = () => {
        const objectClass = props.OBJECT_CLASS || props.CATEGORY_NAME;
        const layer = props.LAYER || props.layer; // Support both uppercase and lowercase
        if (layer === 'cfn:uprn') {
            return {
                type: 'UPRN',
                icon: 'fas fa-home',
                color: '#10b981', // green
                title: `UPRN ${props.UPRN || 'Unknown'}`,
                subtitle: props.ADDRESS_LINE_1 || props.ADDRESS,
                network: undefined,
                layer: 'UPRN Dataset'
            };
        }
        if (objectClass === 'POLE') {
            const dpIdent = props.DP_IDENTIFIER;
            const subPole = props.SUB_POLE && props.SUB_POLE !== '00' ? ` - ${props.SUB_POLE}` : '';
            const objectId = props.OBJECTID || props.DB_ID || '';
            return {
                type: 'POLE',
                icon: 'fas fa-circle',
                color: '#3b82f6', // blue
                title: dpIdent ? `DP${dpIdent}${subPole}` : `Pole`,
                subtitle: objectId ? `[${objectId}]` : undefined,
                network: props.NETWORK || 'Unknown',
                layer: 'Infrastructure'
            };
        }
        if (String(props.PLANT_ITEM || '').toUpperCase() === 'TOBY') {
            const status = props.INVENTORY_STATUS_CODE === 'PDA' ? 'Pending Addition' : 'Installed';
            return {
                type: 'TOBY BOX',
                icon: 'fas fa-house-user',
                color: '#0d9488',
                title: 'Toby Box',
                subtitle: props.UPRN ? `${status} • UPRN ${props.UPRN}` : `${status} • No premises assigned`,
                network: 'CFN',
                layer: 'Infrastructure'
            };
        }
        if (String(props.PLANT_ITEM || '').toUpperCase() === 'CTB' || objectClass === 'CTB') {
            const status = props.INVENTORY_STATUS_CODE === 'PDA' ? 'Pending Addition' : 'Installed';
            const served = ctbSummary
                ? `${ctbSummary.served} served`
                : null;
            const ways = ctbSummary ? (ctbSummary.ways ? `1:${ctbSummary.ways}` : 'no splitter') : null;
            const subtitle = [status, served, ways].filter(Boolean).join(' • ');
            return {
                type: 'CTB',
                icon: 'fas fa-plug',
                color: '#0d9488',
                title: 'Customer Termination Box',
                subtitle,
                network: 'CFN',
                layer: 'Infrastructure'
            };
        }
        if (['MH', 'JB', 'JOINTING CHAMBER'].includes(objectClass)) {
            const structNum = props.STRUCTURE_NUMBER || props.OBJECT_ID;
            const plantItem = props.PLANT_ITEM;
            return {
                type: objectClass === 'MH' ? 'MANHOLE' : 'JOINT BOX',
                icon: 'fas fa-square',
                color: '#f59e0b', // amber
                title: plantItem || 'Chamber',
                subtitle: structNum ? `(${structNum})` : props.OBJECTID ? `[${props.OBJECTID}]` : undefined,
                network: props.NETWORK || 'Unknown',
                layer: 'Infrastructure'
            };
        }
        if (objectClass === 'CABINET' || objectClass === 'CAB') {
            return {
                type: 'CABINET',
                icon: 'fas fa-server',
                color: '#8b5cf6', // purple
                title: props.PLANT_ITEM || 'Cabinet',
                subtitle: props.OBJECTID ? `[${props.OBJECTID}]` : undefined,
                network: props.NETWORK || 'Unknown',
                layer: 'Infrastructure'
            };
        }
        if (layer === 'cfn:pia_duct' || layer === 'cfn:conduit') {
            // For multi-conduit, show count in header
            if (isMultiConduit) {
                return {
                    type: 'DUCT',
                    icon: 'fas fa-minus',
                    color: '#ec4899', // pink
                    title: `${allConduitFeatures.length} Duct Segments`,
                    subtitle: undefined,
                    network: props.NETWORK || 'PIA',
                    layer: 'Infrastructure'
                };
            }
            return {
                type: 'DUCT',
                icon: 'fas fa-minus',
                color: '#ec4899', // pink
                title: props.section_number || props.SECTION_NUMBER || 'Duct Segment',
                subtitle: props.OBJECTID ? `[${props.OBJECTID}]` : undefined,
                network: props.NETWORK || props.network || 'Unknown',
                layer: 'Infrastructure'
            };
        }
        if (layer === 'cfn:leadinRoutes' || layer === 'cfn:inferredLeadIns') {
            return {
                type: 'LEAD-IN',
                icon: 'fas fa-project-diagram',
                color: '#14b8a6', // teal
                title: 'Lead-in Route',
                subtitle: props.ROUTE_ID || undefined,
                network: props.NETWORK || 'CFN',
                layer: layer.includes('inferred') ? 'Inferred' : 'Documented'
            };
        }
        // BFT (Blown Fibre Tubing)
        if (layer === 'cfn:bft' || layer === 'BFT' || props.our_ref || props.OUR_REF) {
            // For multi-BFT, show generic header - details in nested tabs
            if (isMultiBft) {
                return {
                    type: 'BFT',
                    icon: 'fas fa-route',
                    color: '#7c3aed', // violet
                    title: `${allBftFeatures.length} BFT Routes`,
                    subtitle: undefined,
                    network: 'CFN',
                    layer: 'BFT'
                };
            }
            // Single BFT - show its details in header
            return {
                type: 'BFT',
                icon: 'fas fa-route',
                color: '#7c3aed', // violet
                title: props.our_ref || props.OUR_REF || 'BFT Route',
                subtitle: props.description || props.DESCRIPTION || undefined,
                network: 'CFN',
                layer: 'BFT'
            };
        }
        // Check for uploads/photos - support both uppercase LAYER and lowercase layer
        const layerLower = (layer || props.layer || '').toLowerCase();
        if (layer === 'cfn:uploads' || layerLower === 'cfn:uploads') {
            return {
                type: 'PHOTO',
                icon: 'fas fa-camera',
                color: '#06b6d4', // cyan
                title: 'Photo Location',
                subtitle: undefined,
                network: undefined,
                layer: 'Photos'
            };
        }
        // Default fallback
        return {
            type: props.TYPE || 'OBJECT',
            icon: 'fas fa-map-marker-alt',
            color: '#64748b', // slate
            title: props.TITLE || props.NAME || 'Map Object',
            subtitle: props.OBJECTID || props.ID || undefined,
            network: props.NETWORK,
            layer: layer || 'Unknown'
        };
    };
    const metadata = getObjectMetadata();
    // Compute objectType and objectId for the upload hook
    const objectType = metadata.type === 'UPRN' ? 'UPRN' : 'structure';
    const objectId = metadata.type === 'UPRN'
        ? props.UPRN
        : (props.DB_ID || props.OBJECTID || props.ID);
    const structureCoords = props.EASTING && props.NORTHING
        ? { easting: parseInt(props.EASTING), northing: parseInt(props.NORTHING) }
        : undefined;
    // Photo upload hook - provides camera/upload functionality
    const { isUploading, triggerCamera, triggerUpload, renderFileInputs, } = usePhotoUpload({
        objectType: objectType,
        objectId,
        structureCoords,
    });
    // Generate shareable link for this object
    const getShareableLink = () => {
        const baseUrl = window.location.origin + window.location.pathname;
        if (metadata.type === 'UPRN' && props.UPRN) {
            return `${baseUrl}#/uprn/${props.UPRN}`;
        }
        if (metadata.type === 'DUCT' && props.OBJECTID) {
            return `${baseUrl}#/conduitByObjectId/${props.OBJECTID}`;
        }
        // For structures, prefer OBJECTID for the link (more stable/shareable)
        if (['POLE', 'MANHOLE', 'JOINT BOX', 'TOBY BOX', 'CTB', 'CABINET'].includes(metadata.type) && props.OBJECTID) {
            return `${baseUrl}#/structureByObjectId/${props.OBJECTID}`;
        }
        // Fallback to database ID if no OBJECTID
        if (props.DB_ID) {
            return `${baseUrl}#/structureById/${props.DB_ID}`;
        }
        return null;
    };
    // Copy shareable link to clipboard
    const handleCopyLink = async () => {
        const link = getShareableLink();
        if (!link)
            return;
        try {
            await navigator.clipboard.writeText(link);
            setLinkCopied(true);
            setTimeout(() => setLinkCopied(false), 2000);
        }
        catch (err) {
            // Fallback for older browsers
            const textArea = document.createElement('textarea');
            textArea.value = link;
            textArea.style.position = 'fixed';
            textArea.style.left = '-999999px';
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            setLinkCopied(true);
            setTimeout(() => setLinkCopied(false), 2000);
        }
    };
    const shareableLink = getShareableLink();
    // Check if this object type should have aggregation nodes tab
    const shouldShowAggNodesTab = ['POLE', 'MANHOLE', 'JOINT BOX', 'TOBY BOX', 'CTB', 'CABINET'].includes(metadata.type) &&
        props.DB_ID;
    // Load aggregation nodes
    const loadAggNodes = async () => {
        if (!props.DB_ID)
            return;
        setAggNodesLoading(true);
        try {
            const res = await fetch(`/agg_node?structure_id=${props.DB_ID}`);
            const data = await res.json();
            setAggNodes(data.results || []);
            setAggNodesLoaded(true);
        }
        catch (err) {
            console.error('Failed to load aggregation nodes:', err);
            info({
                title: 'Load Error',
                message: 'Failed to load aggregation nodes',
                variant: 'error'
            });
        }
        finally {
            setAggNodesLoading(false);
        }
    };
    // Load aggregation nodes when tab is clicked
    useEffect(() => {
        if (activeTab === 'aggNodes' && !aggNodesLoaded && props.DB_ID) {
            loadAggNodes();
        }
    }, [activeTab, aggNodesLoaded, props.DB_ID]);
    // Served count and splitter size are not carried on the map feature.
    useEffect(() => {
        const isCtb = String(props.PLANT_ITEM || '').toUpperCase() === 'CTB';
        if (!isCtb || !props.DB_ID) {
            setCtbSummary(null);
            return;
        }
        const controller = new AbortController();
        fetch(`/structure/${props.DB_ID}/premises-box`, {
            headers: { Accept: 'application/json' },
            signal: controller.signal,
        })
            .then(response => (response.ok ? response.json() : null))
            .then(box => {
            if (!box)
                return;
            setCtbSummary({
                served: (box.served_uprns || []).length,
                ways: box.split_ways ?? null,
            });
        })
            .catch(() => { });
        return () => controller.abort();
    }, [props.PLANT_ITEM, props.DB_ID]);
    // Close "More" dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (moreActionsRef.current && !moreActionsRef.current.contains(event.target)) {
                setShowMoreActions(false);
            }
        };
        if (showMoreActions) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [showMoreActions]);
    // Track whether we pushed a history state for the gallery
    const galleryHistoryPushedRef = useRef(false);
    // Handle gallery open with browser history
    const openGallery = useCallback(() => {
        setGalleryOpen(true);
        window.history.pushState({ galleryOpen: true }, '');
        galleryHistoryPushedRef.current = true;
    }, []);
    // Close gallery handler - handles both X button and back button
    const closeGallery = useCallback((viaBackButton = false) => {
        setGalleryOpen(false);
        // If we pushed a history state and this isn't being closed via the back button,
        // we need to pop the history state to clean up
        if (galleryHistoryPushedRef.current && !viaBackButton) {
            galleryHistoryPushedRef.current = false;
            // Check if current state has our gallery flag before going back
            if (window.history.state?.galleryOpen) {
                window.history.back();
            }
        }
        else {
            galleryHistoryPushedRef.current = false;
        }
    }, []);
    // Listen for back button to close gallery
    useEffect(() => {
        if (!galleryOpen)
            return;
        const handlePopState = (event) => {
            // Don't close if a child dialog (lightbox) is still open
            if (event.state?.lightboxOpen) {
                return;
            }
            if (!event.state?.galleryOpen) {
                // Close via back button - don't trigger another history.back()
                closeGallery(true);
            }
        };
        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [galleryOpen, closeGallery]);
    // Handle add/edit aggregation node success
    const handleAggNodeSuccess = () => {
        setShowAggNodeForm(false);
        setEditingAggNode(null);
        setAggNodesLoaded(false);
        loadAggNodes();
    };
    // Handle add aggregation node
    const handleAddAggNode = () => {
        setEditingAggNode(null);
        setShowAggNodeForm(true);
    };
    // Handle edit aggregation node
    const handleEditAggNode = (node) => {
        setEditingAggNode(node);
        setShowAggNodeForm(true);
    };
    // Get all displayable properties in a single list
    const getDisplayProperties = () => {
        const properties = [];
        const displayed = new Set();
        // Skip certain technical/internal fields
        const skipFields = new Set([
            'BBOX', 'OBJECT_ID', 'CLOSEST_POINT', 'LAYER', 'TITLE', 'NAME',
            'OBJECT_CLASS', 'CATEGORY_NAME', 'TYPE', 'DB_ID', 'OBJECTID', 'ID',
            'URL', 'DP_IDENTIFIER', 'SUB_POLE', 'STRUCTURE_NUMBER', 'PLANT_ITEM'
        ]);
        // Add exchange code if present
        if (props.EXCHANGE_1141_CODE) {
            properties.push({ key: 'Exchange', value: props.EXCHANGE_1141_CODE });
            displayed.add('EXCHANGE_1141_CODE');
        }
        // Add location as single field if both present
        if (props.EASTING && props.NORTHING) {
            properties.push({
                key: 'Location',
                value: `${props.EASTING}, ${props.NORTHING}`
            });
            displayed.add('EASTING');
            displayed.add('NORTHING');
        }
        // Add network if not already in header
        if (props.NETWORK && !metadata.network) {
            properties.push({ key: 'Network', value: props.NETWORK });
            displayed.add('NETWORK');
        }
        // Combine pole-specific fields
        if (metadata.type === 'POLE' && (props.POLE_AGE || props.POLE_CLASS || props.POLE_TYPE || props.POLE_YEAR)) {
            properties.push({
                key: 'Age/Cls/Typ/Yr',
                value: `${props.POLE_AGE || '?'} / ${props.POLE_CLASS || '?'} / ${props.POLE_TYPE || '?'} / ${props.POLE_YEAR || '?'}`
            });
            displayed.add('POLE_AGE');
            displayed.add('POLE_CLASS');
            displayed.add('POLE_TYPE');
            displayed.add('POLE_YEAR');
        }
        // Add all remaining properties
        Object.keys(props).forEach(key => {
            if (!displayed.has(key) && !skipFields.has(key) && props[key] !== null && props[key] !== undefined && props[key] !== '') {
                properties.push({
                    key: key.replace(/_/g, ' '),
                    value: props[key]
                });
            }
        });
        return properties;
    };
    const displayProperties = getDisplayProperties();
    // Handle structure deletion
    const handleDelete = async () => {
        // The server deletes by cfn.structure.id. OBJECTID is Openreach's identifier
        // and the same number can be another structure's id, so never send it here.
        const objectId = props.DB_ID;
        if (!objectId) {
            await info({
                title: 'Delete Error',
                message: 'This structure has no database id, so it cannot be deleted here.',
                variant: 'error',
            });
            return;
        }
        const confirmed = await confirm({
            title: `Delete ${metadata.title}?`,
            description: 'This action cannot be undone. The structure will be permanently deleted.',
            confirmText: 'Delete',
            variant: 'destructive',
        });
        if (!confirmed)
            return;
        try {
            setLoading(true);
            const response = await fetch(`/structure/${objectId}`, {
                method: 'DELETE',
            });
            if (response.ok) {
                await info({
                    message: 'Structure deleted successfully',
                    variant: 'success',
                });
                // Redraw the structure and lead-in layers so the deleted structure and
                // the lead-ins removed with it disappear from the map.
                const windowAny = window;
                windowAny.CFN?.getCachedLayer?.('StructureLayer')
                    ?.setParams({ time: Date.now() }, false);
                windowAny.CFN?.getCachedWMSLayers?.('cfn:leadinRoutes')
                    ?.forEach((layer) => layer.setParams({ time: Date.now() }, false));
                windowAny.CFN?.getCachedWMSLayers?.('cfn:inferredLeadIns')
                    ?.forEach((layer) => layer.setParams({ time: Date.now() }, false));
                if (onClose)
                    onClose();
                if (context?.onDelete)
                    context.onDelete(objectId);
            }
            else {
                const body = await response.json().catch(() => ({}));
                const serverMessage = body.error || (Array.isArray(body.errors) ? body.errors.join(', ') : null);
                throw new Error(serverMessage || `Delete failed (${response.status})`);
            }
        }
        catch (error) {
            await info({
                title: 'Delete Error',
                message: error instanceof Error ? error.message : 'Failed to delete structure',
                variant: 'error',
            });
        }
        finally {
            setLoading(false);
        }
    };
    // Handle conduit deletion (for CFN custom conduits)
    const handleDeleteConduit = async () => {
        const dbId = props.DB_ID;
        if (!dbId) {
            await info({
                title: 'Delete Error',
                message: 'Cannot delete: conduit has no database ID',
                variant: 'error',
            });
            return;
        }
        const confirmed = await confirm({
            title: 'Delete Conduit?',
            description: 'This action cannot be undone. The conduit will be permanently deleted.',
            confirmText: 'Delete',
            variant: 'destructive',
        });
        if (!confirmed)
            return;
        try {
            setLoading(true);
            const response = await fetch(`/conduit/${dbId}`, {
                method: 'DELETE',
            });
            if (response.ok) {
                await info({
                    message: 'Conduit deleted successfully',
                    variant: 'success',
                });
                // Close popup
                if (onClose)
                    onClose();
                if (context?.popup)
                    context.popup.remove();
                // Refresh the conduit layer to reflect deletion
                if (window.CFN?.getCachedLayer) {
                    const conduitLayer = window.CFN.getCachedLayer('ConduitLayer');
                    if (conduitLayer) {
                        conduitLayer.setParams({ time: Date.now() }, false);
                    }
                }
                // Dispatch event for map refresh
                window.dispatchEvent(new CustomEvent('cfn:conduit:deleted', {
                    detail: { id: dbId },
                }));
            }
            else {
                const result = await response.json().catch(() => ({}));
                throw new Error(result.error || 'Delete failed');
            }
        }
        catch (error) {
            await info({
                title: 'Delete Error',
                message: error instanceof Error ? error.message : 'Failed to delete conduit',
                variant: 'error',
            });
        }
        finally {
            setLoading(false);
        }
    };
    // Show all lead-ins (including rejected) for a specific structure on the map
    const leadinOverlayKey = props.DB_ID ? `inferredLeadInsLayer::showAll::${props.DB_ID}` : '';
    // Initialise toggle state from any existing overlay
    useEffect(() => {
        if (leadinOverlayKey) {
            const windowAny = window;
            setLeadinsVisible(!!windowAny._leadinOverlays?.[leadinOverlayKey]);
        }
    }, [leadinOverlayKey]);
    const handleShowLeadins = () => {
        const dbId = props.DB_ID;
        if (!dbId)
            return;
        const L = window.L;
        const map = context?.map || window.mymap;
        if (!L || !map)
            return;
        const cqlFilter = `"attachmentStructure" = ${dbId}`;
        const windowAny = window;
        if (windowAny._leadinOverlays?.[leadinOverlayKey]) {
            // Already on the map — remove it
            map.removeLayer(windowAny._leadinOverlays[leadinOverlayKey]);
            delete windowAny._leadinOverlays[leadinOverlayKey];
            setLeadinsVisible(false);
            return;
        }
        const layer = L.tileLayer.wms('/geoserver/wms', {
            layers: 'cfn:leadinRoutes',
            format: 'image/png',
            transparent: 'true',
            minZoom: 12,
            maxZoom: 26,
            cql_filter: cqlFilter,
        });
        layer.addTo(map);
        windowAny._leadinOverlays = windowAny._leadinOverlays || {};
        windowAny._leadinOverlays[leadinOverlayKey] = layer;
        setLeadinsVisible(true);
    };
    // Handle lead-in state change (reject, verify, survey-request, clear)
    const handleSetLeadinState = async (state, actionLabel) => {
        const leadinId = parseInt(props.ID);
        if (!leadinId || isNaN(leadinId)) {
            await info({
                title: 'Error',
                message: 'Cannot update: lead-in has no valid ID',
                variant: 'error',
            });
            return;
        }
        const confirmed = await confirm({
            title: `${actionLabel} Lead-in?`,
            description: `Are you sure you want to ${actionLabel.toLowerCase()} this lead-in?`,
            confirmText: actionLabel,
            variant: state === 'rejected' ? 'destructive' : 'default',
        });
        if (!confirmed)
            return;
        try {
            setLoading(true);
            const response = await fetch('/leadin/setState', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: leadinId, state }),
            });
            if (response.ok) {
                await info({
                    message: `Lead-in ${actionLabel.toLowerCase()}ed successfully`,
                    variant: 'success',
                });
                if (onClose)
                    onClose();
                if (context?.popup)
                    context.popup.remove();
                // Refresh lead-in WMS layers
                const windowAny = window;
                if (windowAny.CFN?.getCachedWMSLayers) {
                    windowAny.CFN.getCachedWMSLayers('cfn:leadinRoutes')
                        .forEach((layer) => layer.setParams({ time: Date.now() }, false));
                    windowAny.CFN.getCachedWMSLayers('cfn:inferredLeadIns')
                        .forEach((layer) => layer.setParams({ time: Date.now() }, false));
                }
                else if (windowAny.LayerAdapter?.getWmsLayersByName) {
                    windowAny.LayerAdapter.getWmsLayersByName('cfn:leadinRoutes')
                        .forEach((layer) => layer.setParams({ time: Date.now() }, false));
                    windowAny.LayerAdapter.getWmsLayersByName('cfn:inferredLeadIns')
                        .forEach((layer) => layer.setParams({ time: Date.now() }, false));
                }
                // Refresh any temporary pole lead-in overlays
                if (windowAny._leadinOverlays) {
                    Object.values(windowAny._leadinOverlays).forEach((layer) => {
                        layer.setParams({ time: Date.now() }, false);
                    });
                }
            }
            else {
                const result = await response.json().catch(() => ({}));
                throw new Error(result.error || `${actionLabel} failed`);
            }
        }
        catch (error) {
            await info({
                title: 'Error',
                message: error instanceof Error ? error.message : `Failed to ${actionLabel.toLowerCase()} lead-in`,
                variant: 'error',
            });
        }
        finally {
            setLoading(false);
        }
    };
    // Generate action buttons based on object type
    const getActionButtons = () => {
        const actions = [];
        // Navigation - use CLOSEST_POINT for line features, or geometry coordinates for points
        // Skip for PHOTO type as navigation is not useful for photo locations
        if (feature.geometry?.coordinates && metadata.type !== 'PHOTO') {
            let lng, lat;
            // For line features, use the CLOSEST_POINT (popup anchor point) if available
            if (props.CLOSEST_POINT) {
                try {
                    const closestPoint = JSON.parse(props.CLOSEST_POINT);
                    [lng, lat] = closestPoint;
                }
                catch (e) {
                    // Fallback to first coordinate if parsing fails
                    [lng, lat] = Array.isArray(feature.geometry.coordinates[0])
                        ? feature.geometry.coordinates[0]
                        : feature.geometry.coordinates;
                }
            }
            else {
                // For point features, use geometry coordinates directly
                [lng, lat] = feature.geometry.coordinates;
            }
            actions.push({
                id: 'navigate',
                icon: 'fas fa-location-arrow',
                label: 'Nav',
                onClick: () => {
                    window.open(`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`, '_blank');
                },
                variant: 'primary'
            });
        }
        // Camera/Upload (for structures and UPRNs)
        if (['POLE', 'MANHOLE', 'JOINT BOX', 'TOBY BOX', 'CTB', 'CABINET', 'UPRN'].includes(metadata.type)) {
            // Camera button - requires GPS proximity check (handled by hook)
            actions.push({
                id: 'camera',
                icon: 'fas fa-camera',
                label: 'Photo',
                onClick: triggerCamera,
            });
            // Upload button - no distance restriction (files already on device)
            actions.push({
                id: 'upload',
                icon: 'fas fa-upload',
                label: 'Upload',
                onClick: triggerUpload,
            });
            actions.push({
                id: 'gallery',
                icon: 'fas fa-images',
                label: 'Pics',
                onClick: () => {
                    openGallery();
                }
            });
        }
        // Show lead-ins for this pole (including rejected)
        if (metadata.type === 'POLE' && props.DB_ID) {
            actions.push({
                id: 'show-leadins',
                icon: 'fas fa-home',
                label: leadinsVisible ? 'Lead-ins \u2713' : 'Lead-ins',
                onClick: handleShowLeadins,
                active: leadinsVisible,
            });
        }
        // Edit actions (for structures)
        if ((metadata.type === 'TOBY BOX' || metadata.type === 'CTB') && props.DB_ID) {
            actions.push({
                id: 'edit-premises-box',
                icon: metadata.type === 'CTB' ? 'fas fa-plug' : 'fas fa-home',
                label: metadata.type === 'CTB' ? 'CTB details' : 'Toby details',
                onClick: () => setShowPremisesBoxDialog(true),
                variant: 'primary'
            });
        }
        if (['POLE', 'MANHOLE', 'JOINT BOX', 'TOBY BOX', 'CTB', 'CABINET'].includes(metadata.type)) {
            console.log('[MapObjectPopup] Adding Move button for type:', metadata.type);
            actions.push({
                id: 'move',
                icon: 'fas fa-arrows-alt',
                label: 'Move',
                onClick: () => {
                    console.log('[MapObjectPopup] Move button clicked!');
                    console.log('[MapObjectPopup] Setting showMoveDialog to true');
                    console.log('[MapObjectPopup] Feature:', feature);
                    console.log('[MapObjectPopup] Context:', context);
                    setShowMoveDialog(true);
                }
            });
        }
        // Delete (for CFN structures)
        if (props.LAYER === 'cfn:pia_structure' && ['OR_UNDOCUMENTED', 'CFN'].includes(props.NETWORK)) {
            actions.push({
                id: 'delete',
                icon: 'fas fa-trash',
                label: 'Delete',
                onClick: handleDelete,
                variant: 'danger'
            });
        }
        // Delete (for CFN custom conduits)
        if (metadata.type === 'DUCT' && props.NETWORK === 'CFN' && props.DB_ID) {
            actions.push({
                id: 'delete-conduit',
                icon: 'fas fa-trash',
                label: 'Delete',
                onClick: handleDeleteConduit,
                variant: 'danger'
            });
        }
        // Lead-in validation actions
        if (metadata.type === 'LEAD-IN' && props.ID) {
            const currentState = props.STATE || props.state;
            if (currentState === 'Speculative') {
                // Speculative lead-ins can be rejected, verified, or survey-requested
                actions.push({
                    id: 'reject-leadin',
                    icon: 'fas fa-times-circle',
                    label: 'Reject',
                    onClick: () => handleSetLeadinState('rejected', 'Reject'),
                    variant: 'danger'
                });
                actions.push({
                    id: 'survey-leadin',
                    icon: 'fas fa-clipboard-check',
                    label: 'Survey',
                    onClick: () => handleSetLeadinState('survey-requested', 'Request Survey'),
                    variant: 'secondary'
                });
                actions.push({
                    id: 'verify-leadin',
                    icon: 'fas fa-check-circle',
                    label: 'Verify',
                    onClick: () => handleSetLeadinState('verified', 'Verify'),
                    variant: 'primary'
                });
            }
            else {
                // Non-speculative lead-ins can be cleared back to Speculative
                actions.push({
                    id: 'clear-leadin',
                    icon: 'fas fa-undo',
                    label: 'Clear',
                    onClick: () => handleSetLeadinState('Speculative', 'Clear'),
                    variant: 'secondary'
                });
            }
        }
        return actions;
    };
    const actions = getActionButtons();
    // Check if this object type needs file inputs and gallery
    const needsFileInputs = ['POLE', 'MANHOLE', 'JOINT BOX', 'TOBY BOX', 'CTB', 'CABINET', 'UPRN'].includes(metadata.type);
    return (_jsxs(_Fragment, { children: [needsFileInputs && renderFileInputs(), needsFileInputs && (_jsx(GalleryModal, { open: galleryOpen, onClose: closeGallery, objectType: objectType, objectId: objectId, objectTitle: metadata.title, structureCoords: structureCoords })), _jsxs("div", { className: "map-object-popup", style: { '--accent-color': metadata.color }, children: [_jsxs("div", { className: "map-object-popup__header", style: { borderLeftColor: metadata.color }, children: [_jsx("div", { className: "map-object-popup__header-icon", style: { backgroundColor: metadata.color }, children: _jsx("i", { className: metadata.icon }) }), _jsxs("div", { className: "map-object-popup__header-content", children: [_jsxs("div", { className: "map-object-popup__title-row", children: [_jsx("h3", { className: "map-object-popup__title", children: metadata.title }), _jsx("span", { className: "map-object-popup__type-badge", children: metadata.type }), shareableLink && (_jsx("button", { className: `map-object-popup__link-btn ${linkCopied ? 'copied' : ''}`, onClick: handleCopyLink, title: linkCopied ? 'Copied!' : 'Copy link to this object', children: _jsx("i", { className: linkCopied ? 'fas fa-check' : 'fas fa-link' }) }))] }), (metadata.subtitle || metadata.network) && (_jsxs("div", { className: "map-object-popup__meta-row", children: [metadata.subtitle && (_jsx("span", { className: "map-object-popup__subtitle", children: metadata.subtitle })), metadata.subtitle && metadata.network && (_jsx("span", { className: "map-object-popup__separator", children: "\u2022" })), metadata.network && (_jsx("span", { className: "map-object-popup__network", children: metadata.network }))] }))] })] }), actions.length > 0 && (_jsxs("div", { className: "map-object-popup__actions", children: [(actions.length <= 6 ? actions : actions.slice(0, 5)).map(action => (_jsxs("button", { id: action.id === 'camera' ? 'camera-trigger' :
                                    action.id === 'gallery' ? 'gallery-trigger' :
                                        action.id === 'move' ? 'MoveStructure' :
                                            action.id === 'delete' ? 'DeleteStructure' : undefined, className: `map-object-popup__action-btn map-object-popup__action-btn--${action.variant || 'secondary'}${action.active ? ' map-object-popup__action-btn--active' : ''}`, onClick: (e) => {
                                    action.onClick();
                                }, title: action.label, children: [_jsx("i", { className: action.icon }), _jsx("span", { children: action.label })] }, action.id))), actions.length > 6 && (_jsxs("div", { ref: moreActionsRef, className: "map-object-popup__more-actions", children: [_jsxs("button", { className: "map-object-popup__action-btn map-object-popup__action-btn--secondary", title: "More actions", onClick: () => setShowMoreActions(!showMoreActions), children: [_jsx("i", { className: "fas fa-ellipsis-h" }), _jsx("span", { children: "More" })] }), showMoreActions && (_jsx("div", { className: "map-object-popup__more-dropdown", children: actions.slice(5).map(action => (_jsxs("button", { className: "map-object-popup__more-item", onClick: () => {
                                                action.onClick();
                                                setShowMoreActions(false);
                                            }, children: [_jsx("i", { className: action.icon }), _jsx("span", { children: action.label })] }, action.id))) }))] }))] })), shouldShowAggNodesTab && (_jsx("div", { className: "map-object-popup__tabs", children: _jsxs("div", { className: "map-object-popup__tab-list", children: [_jsx("button", { className: `map-object-popup__tab ${activeTab === 'main' ? 'active' : ''}`, onClick: () => setActiveTab('main'), children: "Struct" }), _jsxs("button", { className: `map-object-popup__tab ${activeTab === 'aggNodes' ? 'active' : ''}`, onClick: () => setActiveTab('aggNodes'), children: ["Nodes ", aggNodesLoaded && `(${aggNodes.length})`] })] }) })), (!shouldShowAggNodesTab || activeTab === 'main') && (_jsxs(_Fragment, { children: [metadata.type === 'PHOTO' && feature.geometry?.coordinates && (_jsx(PhotoMiniGallery, { coordinates: feature.geometry.coordinates, radius: 5 })), metadata.type === 'BFT' && (_jsxs("div", { className: "map-object-popup__bft-content", children: [isMultiBft && (_jsx("div", { className: "map-object-popup__bft-tabs", children: allBftFeatures.map((bftFeature, idx) => {
                                            const tabProps = bftFeature.properties || {};
                                            const tabRef = tabProps.our_ref || tabProps.OUR_REF;
                                            const tabDesc = tabProps.description || tabProps.DESCRIPTION;
                                            return (_jsx("button", { className: `map-object-popup__bft-tab ${idx === activeBftIndex ? 'active' : ''}`, onClick: () => setActiveBftIndex(idx), title: tabDesc || '', children: tabRef || `BFT ${idx + 1}` }, tabRef || idx));
                                        }) })), (() => {
                                        const bftProps = isMultiBft ? allBftFeatures[activeBftIndex]?.properties : props;
                                        if (!bftProps)
                                            return null;
                                        // Handle both lowercase (from API) and uppercase (after featureFilter) property names
                                        const ourRef = bftProps.our_ref || bftProps.OUR_REF;
                                        const description = bftProps.description || bftProps.DESCRIPTION;
                                        const cableType = bftProps.cableType || bftProps.CABLETYPE;
                                        const status = bftProps.status || bftProps.STATUS;
                                        const rfs = bftProps.rfs !== undefined ? bftProps.rfs : bftProps.RFS;
                                        const pianoi = bftProps.PIANOI;
                                        const objectid = bftProps.objectid || bftProps.OBJECTID;
                                        return (_jsx("div", { className: "map-object-popup__properties-section", children: _jsxs("dl", { className: "map-object-popup__properties", children: [ourRef && (_jsxs(_Fragment, { children: [_jsx("dt", { children: "Reference" }), _jsx("dd", { children: _jsx("a", { href: `/bft/${ourRef}`, target: "_blank", rel: "noopener noreferrer", children: ourRef }) })] })), description && (_jsxs(_Fragment, { children: [_jsx("dt", { children: "Description" }), _jsx("dd", { children: description })] })), cableType && (_jsxs(_Fragment, { children: [_jsx("dt", { children: "Cable Type" }), _jsx("dd", { children: cableType })] })), status && (_jsxs(_Fragment, { children: [_jsx("dt", { children: "Status" }), _jsx("dd", { children: _jsx("span", { style: {
                                                                        color: status === 'Installed' ? '#28a745' :
                                                                            status === 'Planned' ? '#007bff' :
                                                                                status === 'In Progress' ? '#ffc107' : '#6c757d',
                                                                        fontWeight: 'bold'
                                                                    }, children: status }) })] })), rfs !== undefined && (_jsxs(_Fragment, { children: [_jsx("dt", { children: "RFS" }), _jsx("dd", { children: rfs ? 'Yes' : 'No' })] })), pianoi && (_jsxs(_Fragment, { children: [_jsx("dt", { children: "PIA NOI" }), _jsx("dd", { children: pianoi })] })), objectid && (_jsxs(_Fragment, { children: [_jsx("dt", { children: "Object ID" }), _jsx("dd", { children: objectid })] }))] }) }));
                                    })()] })), metadata.type === 'DUCT' && (_jsxs("div", { className: "map-object-popup__conduit-content", children: [isMultiConduit && (_jsx("div", { className: "map-object-popup__conduit-tabs", children: allConduitFeatures.map((conduitFeature, idx) => {
                                            const tabProps = conduitFeature.properties || {};
                                            const tabSection = tabProps.section_number || tabProps.SECTION_NUMBER;
                                            const tabSpan = tabProps.span_name || tabProps.SPAN_NAME;
                                            const typeName = tabProps.type_name || tabProps.TYPE_NAME;
                                            // Use "Aerial" prefix for aerial conduits, "Duct" for others
                                            const fallbackLabel = typeName === 'AERIAL' ? `Aerial ${idx + 1}` : `Duct ${idx + 1}`;
                                            return (_jsx("button", { className: `map-object-popup__conduit-tab ${idx === activeConduitIndex ? 'active' : ''}`, onClick: () => setActiveConduitIndex(idx), title: tabSpan || '', children: tabSection || tabSpan || fallbackLabel }, tabSection || tabSpan || idx));
                                        }) })), (() => {
                                        const conduitProps = isMultiConduit ? allConduitFeatures[activeConduitIndex]?.properties : props;
                                        if (!conduitProps)
                                            return null;
                                        // Handle both lowercase (from API) and uppercase (after featureFilter) property names
                                        const sectionNumber = conduitProps.section_number || conduitProps.SECTION_NUMBER;
                                        const spanName = conduitProps.span_name || conduitProps.SPAN_NAME;
                                        const typeName = conduitProps.type_name || conduitProps.TYPE_NAME;
                                        const numberOfWays = conduitProps.number_of_ways || conduitProps.NUMBER_OF_WAYS;
                                        const measuredLength = conduitProps.measured_length || conduitProps.MEASURED_LENGTH;
                                        const ragStatus = conduitProps.rag_status || conduitProps.RAG_STATUS;
                                        const inventoryStatus = conduitProps.inventory_status_code || conduitProps.INVENTORY_STATUS_CODE;
                                        const ownershipType = conduitProps.ownership_type_code || conduitProps.OWNERSHIP_TYPE_CODE;
                                        const exchangeCode = conduitProps.exchange_1141_code || conduitProps.EXCHANGE_1141_CODE;
                                        const network = conduitProps.network || conduitProps.NETWORK;
                                        const noiRoutes = conduitProps.ORREFERENCEIDS;
                                        const objectid = conduitProps.objectid || conduitProps.OBJECTID;
                                        const ragMap = { 'G': 'Green', 'A': 'Amber', 'R': 'Red' };
                                        const ragColour = { 'G': '#28a745', 'A': '#ffc107', 'R': '#dc3545' };
                                        return (_jsx("div", { className: "map-object-popup__properties-section", children: _jsxs("dl", { className: "map-object-popup__properties", children: [sectionNumber && (_jsxs(_Fragment, { children: [_jsx("dt", { children: "Section" }), _jsx("dd", { children: sectionNumber })] })), spanName && (_jsxs(_Fragment, { children: [_jsx("dt", { children: "Span" }), _jsx("dd", { children: spanName })] })), typeName && (_jsxs(_Fragment, { children: [_jsx("dt", { children: "Type" }), _jsx("dd", { children: typeName })] })), numberOfWays && (_jsxs(_Fragment, { children: [_jsx("dt", { children: "Ways" }), _jsx("dd", { children: numberOfWays })] })), measuredLength && (_jsxs(_Fragment, { children: [_jsx("dt", { children: "Length" }), _jsxs("dd", { children: [parseFloat(measuredLength).toFixed(2), "m"] })] })), ragStatus && (_jsxs(_Fragment, { children: [_jsx("dt", { children: "RAG Status" }), _jsx("dd", { children: _jsx("span", { style: {
                                                                        color: ragColour[ragStatus] || '#6c757d',
                                                                        fontWeight: 'bold'
                                                                    }, children: ragMap[ragStatus] || ragStatus }) })] })), inventoryStatus && (_jsxs(_Fragment, { children: [_jsx("dt", { children: "Inventory" }), _jsx("dd", { children: inventoryStatus })] })), ownershipType && (_jsxs(_Fragment, { children: [_jsx("dt", { children: "Ownership" }), _jsx("dd", { children: ownershipType })] })), exchangeCode && (_jsxs(_Fragment, { children: [_jsx("dt", { children: "Exchange" }), _jsx("dd", { children: exchangeCode })] })), network && (_jsxs(_Fragment, { children: [_jsx("dt", { children: "Network" }), _jsx("dd", { children: network })] })), noiRoutes && (_jsxs(_Fragment, { children: [_jsx("dt", { children: "NOI Routes" }), _jsx("dd", { children: noiRoutes })] })), objectid && (_jsxs(_Fragment, { children: [_jsx("dt", { children: "Object ID" }), _jsx("dd", { children: objectid })] }))] }) }));
                                    })()] })), metadata.type !== 'PHOTO' && metadata.type !== 'BFT' && metadata.type !== 'DUCT' && displayProperties.length > 0 && (_jsx("div", { className: "map-object-popup__properties-section", children: _jsx("dl", { className: "map-object-popup__properties", children: displayProperties.map((prop, idx) => (_jsxs(React.Fragment, { children: [_jsx("dt", { children: prop.key }), _jsx("dd", { children: prop.link ? (_jsx("a", { href: prop.link, target: "_blank", rel: "noopener noreferrer", children: prop.value })) : (prop.value) })] }, idx))) }) })), feature.geometry?.type === 'Point' && metadata.type !== 'UPRN' && metadata.type !== 'PHOTO' && (_jsxs("div", { className: "map-object-popup__footer", children: [_jsxs("button", { className: "map-object-popup__cta-btn map-object-popup__cta-btn--primary", id: "startPath", children: [_jsx("i", { className: "fas fa-play-circle" }), "Start Route"] }), _jsxs("button", { className: "map-object-popup__cta-btn map-object-popup__cta-btn--secondary", id: "endPath", children: [_jsx("i", { className: "fas fa-stop-circle" }), "End Route"] })] }))] })), shouldShowAggNodesTab && activeTab === 'aggNodes' && (_jsx("div", { className: "map-object-popup__agg-nodes", children: aggNodesLoading ? (_jsx("div", { className: "map-object-popup__loading", children: _jsx("div", { className: "map-object-popup__spinner", role: "status", "aria-label": "Loading" }) })) : aggNodes.length === 0 ? (_jsxs("div", { className: "map-object-popup__empty", children: [_jsx("p", { children: "No nodes on this structure." }), _jsxs("button", { className: "map-object-popup__add-btn", onClick: handleAddAggNode, children: [_jsx("i", { className: "fas fa-plus-square" }), " Add Node"] })] })) : (_jsxs(_Fragment, { children: [_jsx("div", { className: "map-object-popup__agg-nodes-list", children: aggNodes.map(node => (_jsxs("div", { className: "map-object-popup__agg-node", children: [_jsxs("div", { className: "map-object-popup__agg-node-header", children: [_jsx("span", { className: "map-object-popup__agg-node-type", children: node.node_type }), _jsx("span", { className: "map-object-popup__agg-node-id", children: node.node_cfn_id || (node.node_type === 'VIRT' ? 'Virtual Node' : 'Unknown') })] }), node.node_bt_noi && (_jsx("div", { className: "map-object-popup__agg-node-noi", children: node.node_bt_noi })), node.node_description && (_jsx("div", { className: "map-object-popup__agg-node-description", children: node.node_description })), _jsxs("div", { className: "map-object-popup__agg-node-actions", children: [node.node_cfn_id && (_jsx("button", { className: "map-object-popup__agg-node-btn", onClick: () => {
                                                            window.open(`https://portal.cambridgefibre.uk/cfn/physnode/by_name/${node.node_cfn_id}`, '_blank');
                                                        }, title: "Open in Portal", children: _jsx("i", { className: "fa fa-external-link-alt" }) })), _jsx("button", { className: "map-object-popup__agg-node-btn", onClick: () => handleEditAggNode(node), title: "Edit", children: _jsx("i", { className: "fas fa-edit" }) }), node.node_type !== 'CTB' && context?.map && (_jsx("button", { className: "map-object-popup__agg-node-btn", onClick: () => {
                                                            openNodeMovePanel({
                                                                map: context.map,
                                                                node,
                                                                fromStructureId: props.DB_ID,
                                                                onMoved: () => {
                                                                    const windowAny = window;
                                                                    windowAny.CFN?.getCachedLayer?.('StructureLayer')
                                                                        ?.setParams({ time: Date.now() }, false);
                                                                    windowAny.CFN?.getCachedWMSLayers?.('cfn:phys_node')
                                                                        ?.forEach((layer) => layer.setParams({ time: Date.now() }, false));
                                                                },
                                                            });
                                                            context?.popup?.remove();
                                                        }, title: "Move to another structure", children: _jsx("i", { className: "fas fa-people-carry" }) }))] })] }, node.id))) }), _jsxs("button", { className: "map-object-popup__add-btn", onClick: handleAddAggNode, children: [_jsx("i", { className: "fas fa-plus-square" }), " Add Node"] })] })) })), showAggNodeForm && (_jsx(AggregationNodeForm, { structureId: props.DB_ID, structureClass: props.OBJECT_CLASS || props.CATEGORY_NAME, structureTitle: metadata.title, structureSubtitle: metadata.subtitle, existingNode: editingAggNode, onSuccess: handleAggNodeSuccess, onCancel: () => {
                            setShowAggNodeForm(false);
                            setEditingAggNode(null);
                        } })), (() => {
                        console.log('[MapObjectPopup] Render: showMoveDialog=', showMoveDialog, 'context.map=', context?.map);
                        return showMoveDialog && context?.map && (_jsx(StructureMoveDialog, { open: showMoveDialog, onClose: () => setShowMoveDialog(false), feature: feature, map: context.map, onDragModeStart: () => {
                                // Hide the structure popup when drag mode starts (but don't remove it!)
                                if (context?.popup && context.popup._container) {
                                    console.log('[MapObjectPopup] Hiding popup for drag mode');
                                    context.popup._container.style.display = 'none';
                                }
                            }, onDragModeEnd: () => {
                                // Show the structure popup again when drag mode ends
                                if (context?.popup && context.popup._container) {
                                    console.log('[MapObjectPopup] Showing popup again');
                                    context.popup._container.style.display = '';
                                }
                            }, onSuccess: () => {
                                // Close the popup after successful move
                                if (context?.popup) {
                                    context.popup.remove();
                                }
                            } }));
                    })(), showPremisesBoxDialog && props.DB_ID && (_jsx(PremisesBoxDialog, { open: showPremisesBoxDialog, onClose: () => setShowPremisesBoxDialog(false), structureId: props.DB_ID, easting: Number(props.EASTING), northing: Number(props.NORTHING), plantItem: metadata.type === 'CTB' ? 'CTB' : 'TOBY', onSuccess: () => {
                            const windowAny = window;
                            windowAny.CFN?.getCachedLayer?.('StructureLayer')
                                ?.setParams({ time: Date.now() }, false);
                            windowAny.CFN?.getCachedWMSLayers?.('cfn:leadinRoutes')
                                ?.forEach((layer) => layer.setParams({ time: Date.now() }, false));
                            windowAny.CFN?.getCachedWMSLayers?.('cfn:inferredLeadIns')
                                ?.forEach((layer) => layer.setParams({ time: Date.now() }, false));
                            context?.popup?.remove();
                        } }))] })] }));
};
export default MapObjectPopup;
